import React, { Fragment } from 'react';

import {
  Box,
  Card,
  CardActions,
  CardContent,
  Divider,
  Grid,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import _, { compact, lowerCase } from 'lodash';
import Image from 'next/image';
import ConfigContext from 'src/context/config';
import useNonNullContext from 'src/hooks/useNonNullContext';
import { LegacyAllergenCategories } from 'src/ts/enums/ingredientsToAvoid.enums';
import { TagCategories, Tags } from 'src/ts/enums/tags.enums';
import { getDietaryTemplatesFromTConfig } from 'src/utils/catalog-items';
import AddRemoveMeal from '../AddRemoveMeal';
import MealDetailsModal from '../MealDetailsModal/MealDetailsModal';

import {
  IngredientToAvoid,
  MacroNutrients,
  Tag,
} from '../../ts/@types/generated/graphql/catalog-service/graphql';
import Badge from './Badge';
import ChefHatIcon from '../Icons/ChefHat';

type MealTags = Array<Tag | null>;

type MealCardTags = {
  spicyTags: MealTags;
  servingsTags: MealTags;
  celebrityChefTags: MealTags;
  territoryServesTags: MealTags;
  newTags: MealTags;
  improvedRecipeTags: MealTags;
  seasonalTags: MealTags;
};

type MealCardVariant = {
  id: number;
  macroNutrients: MacroNutrients;
  containsAllergensCopy: string;
  publicIngredientsCopy: string;
  nutritionCaveat?: string | null;
  ingredientsToAvoid: IngredientToAvoid[];
  tags: MealTags | null;
  product: {
    imageUrl?: string | null;
    title: string;
    gardenMealId?: number | null;
    sku?: {
      sku?: string | null;
    };
    subtitle?: string | null;
    designer: { name: string };
    description?: string | null;
  };
};

type MealCardProps = {
  mealVariantData: MealCardVariant;
  mealPrice: number;
  mealTags?: MealCardTags;
  soldOut?: boolean;
  quantityInCart: number;
  editCartFunctions?: {
    addMealToCart: (variantId: number) => void;
    removeMealFromCart: (variantId: number) => void;
  };
  mealDetailsModalOptions?: {
    enableMealDetailsModal: boolean;
    tagFilterType: 'funnel' | 'postCheckout';
  };
  featuredSkuGroups: string[][];
};

const MealCard: React.FC<MealCardProps> = ({
  mealVariantData,
  mealPrice,
  mealTags,
  soldOut,
  quantityInCart,
  editCartFunctions,
  mealDetailsModalOptions,
  featuredSkuGroups,
}) => {
  const theme = useTheme();
  const configContext = useNonNullContext(ConfigContext);
  const {
    mealDetailProfile: { showMacros, showSupplier, showPrice, useNetCarbs },
  } = configContext;
  const { primary, secondary } = theme.palette;
  const isSmallScreen = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('sm'),
  );

  const enableMealDetailsModal =
    mealDetailsModalOptions?.enableMealDetailsModal;

  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const {
    imageUrl: mealImgUrl,
    title,
    subtitle,
    designer,
  } = mealVariantData.product;
  const designerNames = [designer.name];
  const priceInDollars = (mealPrice / 100).toFixed(2);

  const secondaryBadge =
    mealTags && (mealTags.spicyTags[0] || mealTags.servingsTags[0]);
  const primaryBadge =
    mealTags &&
    (mealTags.celebrityChefTags[0] ||
      mealTags.territoryServesTags[0] ||
      mealTags.newTags[0] ||
      mealTags.improvedRecipeTags[0] ||
      mealTags.seasonalTags[0]);

  const variantId = mealVariantData.id;

  const macros = mealVariantData.macroNutrients;
  const macrosToRender = [['cal', `${Math.round(macros.calories)}`]];
  macrosToRender.push(
    useNetCarbs
      ? ['net carbs', `${Math.round(macros.netCarbsInG)}g`]
      : ['carbs', `${Math.round(macros.totalCarbsInG)}g`],
  );
  macrosToRender.push(
    ['protein', `${Math.round(macros.proteinInG)}g`],
    ['fat', `${Math.round(macros.totalFatInG)}g`],
  );

  const showBadge = !editCartFunctions;

  const allergens =
    mealVariantData?.ingredientsToAvoid &&
    compact(
      mealVariantData.ingredientsToAvoid.map((ingredientToAvoid) => {
        if (ingredientToAvoid.isAllergen) {
          return lowerCase(ingredientToAvoid.displayName);
        }
      }),
    ).join(', ');

  const kosherTag =
    mealVariantData?.tags &&
    mealVariantData.tags.find(
      (tag) => tag?.category?.name === 'KOSHER_CERTIFICATION',
    );

  const mealSku = mealVariantData.product.sku?.sku;
  const isFeaturedMeal =
    !!mealSku &&
    featuredSkuGroups.some((skuGroup) => skuGroup.includes(mealSku));

  /**
   * We ONLY want to show information about sourcing standards, if there's no antibiotics AND there's animal proteins on the meal.
   */
  const isNaeProteinSourcingStandard = (() => {
    const hasAntibioticsTag = !!mealVariantData?.tags?.some(
      (tag) => tag?.id === Tags.NAE_PROTEIN_SOURCING_STANDARD,
    );
    const hasAnimalProteins = !!mealVariantData?.ingredientsToAvoid?.some(
      (ingredientToAvoid) =>
        ingredientToAvoid.category.legacyAllergenCategoryId ===
        LegacyAllergenCategories.ANIMAL_PROTEINS,
    );

    return hasAntibioticsTag && hasAnimalProteins;
  })();

  const shelfLifeTagId = mealVariantData.tags?.find(
    (tag) => tag?.category.id === TagCategories.SHELF_LIFE,
  )?.id;

  const dietaryTemplateTags = mealVariantData.tags?.filter(
    (tag) => tag?.category.id === TagCategories.DIETARY_TEMPLATE,
  );

  return (
    <>
      <Card
        onClick={() => {
          if (enableMealDetailsModal) {
            setIsModalOpen(true);
          }
        }}
        sx={{
          width: isSmallScreen ? '95%' : '15rem',
          maxWidth: '100vw',
          cursor: enableMealDetailsModal ? 'pointer' : undefined,
          position: 'relative',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ position: 'relative', height: '8.75rem', flexShrink: 0 }}>
          <Image
            src={mealImgUrl ?? ''}
            alt={title}
            fill
            sizes="(max-width: 600px) 95vw, 240px"
            style={{ objectFit: 'cover' }}
          />
          {showBadge && (
            <Badge
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              content={quantityInCart.toString()}
              backgroundColor={primary.light}
            />
          )}
          {secondaryBadge && (
            <Badge
              anchorOrigin={{
                vertical: 'top',
                horizontal: primaryBadge ? 'left' : 'right',
              }}
              content={secondaryBadge.displayValue || secondaryBadge.value}
              backgroundColor={secondary.dark}
            />
          )}
          {mealVariantData.product.sku?.sku && isFeaturedMeal ? (
            <Badge
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              backgroundColor={primary.dark}
              content={
                configContext.featuredMealsSupportMessage ? (
                  <Tooltip
                    title={configContext.featuredMealsSupportMessage}
                    placement="bottom"
                  >
                    <Typography variant="caption">
                      {configContext.featuredMealsTitle}
                    </Typography>
                  </Tooltip>
                ) : (
                  <Typography variant="caption">
                    {configContext.featuredMealsTitle}
                  </Typography>
                )
              }
            />
          ) : null}
          {primaryBadge && (
            <Badge
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              content={primaryBadge.displayValue || primaryBadge.value}
              backgroundColor={primary.dark}
            />
          )}
        </div>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: 'column',
            flex: 1,
          }}
        >
          <CardContent sx={{ padding: '12px 12px 0 12px' }}>
            <Box>
              <Typography
                variant="subtitle2"
                component="div"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: '3',
                  WebkitBoxOrient: 'vertical',
                  fontSize: '1rem',
                }}
              >
                {title} {subtitle}
              </Typography>
            </Box>

            {showSupplier ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  marginBottom: '1.0rem',
                }}
              >
                <ChefHatIcon width={12} height={12} />
                <Typography
                  variant="caption"
                  component="div"
                  sx={{
                    fontSize: '0.875rem',
                  }}
                >
                  {designerNames.join(' & ')}
                </Typography>
              </Box>
            ) : undefined}

            <Grid
              container
              sx={{
                justifyContent: 'space-between',
                whiteSpace: 'nowrap',
                maxWidth: '15rem',
                margin: 'auto',
                marginBottom: '1.0rem',
              }}
            >
              {showMacros
                ? macrosToRender.map(([key, val], idx) => {
                    return (
                      <Fragment key={key}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexDirection: 'column',
                            justifyContent: 'center',
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 'medium',
                              lineHeight: 1.2,
                              fontSize: '0.875rem',
                            }}
                          >
                            {val}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              lineHeight: 1.2,
                              fontSize: '0.875rem',
                            }}
                          >
                            {key}
                          </Typography>
                        </Box>

                        {idx !== Object.keys(macrosToRender).length - 1 && (
                          <Divider orientation="vertical" flexItem />
                        )}
                      </Fragment>
                    );
                  })
                : undefined}
            </Grid>
          </CardContent>
          <CardActions
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              px: '12px 8px',
              paddingTop: 0,
              alignItems: 'center',
            }}
          >
            {soldOut && !quantityInCart ? (
              <Typography
                variant="button"
                gutterBottom
                sx={{
                  color: theme.palette.grey[500],
                  fontSize: '0.875rem',
                }}
              >
                Sold Out
              </Typography>
            ) : (
              showPrice && (
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontSize: '0.875rem',
                  }}
                >
                  ${priceInDollars}
                </Typography>
              )
            )}
            {editCartFunctions ? (
              <AddRemoveMeal
                editCartFunctions={editCartFunctions}
                showRemoveWithZero={false}
                quantityInCart={quantityInCart}
                variantId={variantId}
                soldOut={!!soldOut}
                disableRipple={true}
                fontSize={'1rem'}
                buttonSize={'2rem'}
                quantityWidth={'24px'}
              />
            ) : null}
          </CardActions>
        </Box>
      </Card>
      {mealDetailsModalOptions && enableMealDetailsModal && (
        <MealDetailsModal
          description={mealVariantData.product.description ?? ''}
          close={() => setIsModalOpen(false)}
          containsAllergensCopy={allergens ? allergens : null}
          designer={mealVariantData.product.designer.name}
          dietaryTemplateDisplayNames={_.compact(
            getDietaryTemplatesFromTConfig(
              dietaryTemplateTags as Tag[],
              configContext.visibleTagFilters ?? [],
              mealDetailsModalOptions.tagFilterType,
            ),
          ).map((tagFilter) => tagFilter.displayName)}
          imageUrl={mealVariantData.product.imageUrl ?? ''}
          isOpen={isModalOpen}
          title={mealVariantData.product.title}
          subtitle={mealVariantData.product.subtitle ?? ''}
          macroNutrients={mealVariantData.macroNutrients}
          servingsTag={mealTags?.servingsTags[0]}
          publicIngredientsCopy={mealVariantData.publicIngredientsCopy}
          kosherTagDisplayName={kosherTag?.displayName}
          isNaeProteinSourcingStandard={isNaeProteinSourcingStandard}
          shelfLifeTagId={shelfLifeTagId}
          nutritionCaveat={mealVariantData.nutritionCaveat}
          editCartFunctions={editCartFunctions}
          quantityInCart={quantityInCart}
          variantId={variantId}
          soldOut={!!soldOut}
        />
      )}
    </>
  );
};

export default MealCard;
